import './ckeditor.css';
import 'ckeditor5/ckeditor5.css';

import React, { useEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { Box } from '@mui/material';
import {
  AccessibilityHelp,
  Alignment,
  Autoformat,
  AutoImage,
  AutoLink,
  Autosave,
  BalloonToolbar,
  BlockQuote,
  BlockToolbar,
  Bold,
  ClassicEditor,
  CloudServices,
  Code,
  CodeBlock,
  Essentials,
  FileRepository,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  FullPage,
  GeneralHtmlSupport,
  Heading,
  Highlight,
  HorizontalLine,
  HtmlComment,
  HtmlEmbed,
  ImageBlock,
  ImageCaption,
  ImageInline,
  ImageInsert,
  ImageInsertViaUrl,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  LinkImage,
  List,
  ListProperties,
  Markdown,
  MediaEmbed,
  Mention,
  PageBreak,
  Paragraph,
  PasteFromMarkdownExperimental,
  PasteFromOffice,
  RemoveFormat,
  SelectAll,
  ShowBlocks,
  SimpleUploadAdapter,
  SourceEditing,
  SpecialCharacters,
  SpecialCharactersArrows,
  SpecialCharactersCurrency,
  SpecialCharactersEssentials,
  SpecialCharactersLatin,
  SpecialCharactersMathematical,
  SpecialCharactersText,
  Strikethrough,
  Style,
  Subscript,
  Superscript,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  TextTransformation,
  Underline,
  Undo,
} from 'ckeditor5';
import { observer } from 'mobx-react-lite';

interface CEditorProps {
  name: string;
  placeholder?: string;
  onChangeCallback?: (value: string) => void;
}

const CEditor: React.FC<CEditorProps> = observer(
  ({ name, placeholder, onChangeCallback, ...other }: CEditorProps) => {
    const { control, setValue } = useFormContext();
    const { REACT_APP_API_URL } = process.env;
    const uploadUrl = '/v1/editor/image/upload';

    const editorContainerRef = useRef(null);
    const editorRef = useRef(null);
    const [isLayoutReady, setIsLayoutReady] = useState(false);

    useEffect(() => {
      setIsLayoutReady(true);
      return () => setIsLayoutReady(false);
    }, []);

    const uploadAdapter = (loader: any) => {
      return {
        upload: () => {
          return new Promise((resolve, reject) => {
            const body = new FormData();
            loader.file.then((file: File) => {
              body.append('uploadFile', file);
              fetch(`${REACT_APP_API_URL}${uploadUrl}`, {
                method: 'post',
                body: body,
              })
                .then((res) => res.json())
                .then((res) => {
                  if (res.data) {
                    resolve({
                      default: `${res.data}`,
                    });
                  } else {
                    reject(new Error('파일 업로드 실패'));
                  }
                })
                .catch((err) => {
                  reject(err);
                });
            });
          });
        },
      };
    };

    const uploadPlugin = function (editor: any) {
      editor.plugins.get('FileRepository').createUploadAdapter = (loader: any) => {
        return uploadAdapter(loader);
      };
    };

    const editorConfig = {
      extraPlugins: [uploadPlugin],
      toolbar: {
        items: [
          'undo',
          'redo',
          '|',
          'sourceEditing',
          'showBlocks',
          'selectAll',
          '|',
          'heading',
          'style',
          '|',
          'alignment',
          'fontSize',
          'fontColor',
          'fontBackgroundColor',
          'bold',
          'italic',
          'underline',
          'strikethrough',
          {
            label: 'more',
            withText: true,
            icon: 'threeVerticalDots',
            items: [
              'bulletedList',
              'numberedList',
              'indent',
              'outdent',
              '|',
              'fontFamily',
              'subscript',
              'superscript',
              'code',
              'removeFormat',
              '|',
              'specialCharacters',
              'horizontalLine',
              'pageBreak',
              'link',
            ],
          },
          '|',
          'insertImage',
          'insertImageViaUrl',
          'mediaEmbed',
          '|',
          {
            label: 'Other',
            withText: true,
            icon: 'threeVerticalDots',
            items: ['highlight', 'blockQuote', 'codeBlock', 'htmlEmbed'],
          },

          'insertTable',
          '|',
          // 'accessibilityHelp',
        ],
        shouldNotGroupWhenFull: true,
      },
      plugins: [
        FileRepository,
        AccessibilityHelp,
        Alignment,
        Autoformat,
        AutoImage,
        AutoLink,
        Autosave,
        BalloonToolbar,
        BlockQuote,
        BlockToolbar,
        Bold,
        CloudServices,
        Code,
        CodeBlock,
        Essentials,
        FontBackgroundColor,
        FontColor,
        FontFamily,
        FontSize,
        FullPage,
        GeneralHtmlSupport,
        Heading,
        Highlight,
        HorizontalLine,
        HtmlComment,
        HtmlEmbed,
        ImageBlock,
        ImageCaption,
        ImageInline,
        ImageInsert,
        ImageInsertViaUrl,
        ImageResize,
        ImageStyle,
        ImageTextAlternative,
        ImageToolbar,
        ImageUpload,
        Indent,
        IndentBlock,
        Italic,
        Link,
        LinkImage,
        List,
        ListProperties,
        Markdown,
        MediaEmbed,
        Mention,
        PageBreak,
        Paragraph,
        PasteFromMarkdownExperimental,
        PasteFromOffice,
        RemoveFormat,
        SelectAll,
        ShowBlocks,
        SimpleUploadAdapter,
        SourceEditing,
        SpecialCharacters,
        SpecialCharactersArrows,
        SpecialCharactersCurrency,
        SpecialCharactersEssentials,
        SpecialCharactersLatin,
        SpecialCharactersMathematical,
        SpecialCharactersText,
        Strikethrough,
        Style,
        Subscript,
        Superscript,
        Table,
        TableCaption,
        TableCellProperties,
        TableColumnResize,
        TableProperties,
        TableToolbar,
        TextTransformation,
        Underline,
        Undo,
      ],
      balloonToolbar: [
        'bold',
        'italic',
        '|',
        'link',
        'insertImage',
        '|',
        'bulletedList',
        'numberedList',
      ],
      blockToolbar: [
        'fontSize',
        'fontColor',
        'fontBackgroundColor',
        '|',
        'bold',
        'italic',
        '|',
        'link',
        'insertImage',
        'insertTable',
        '|',
        'bulletedList',
        'numberedList',
        'indent',
        'outdent',
      ],
      fontFamily: {
        supportAllValues: true,
      },
      fontSize: {
        options: [10, 12, 14, 'default', 18, 20, 22],
        supportAllValues: true,
      },
      heading: {
        options: [
          {
            model: 'paragraph' as const,
            title: 'Paragraph' as const,
            class: 'ck-heading_paragraph' as const,
          },
          {
            model: 'heading1' as const,
            view: 'h1' as const,
            title: 'Heading 1' as const,
            class: 'ck-heading_heading1' as const,
          },
          {
            model: 'heading2' as const,
            view: 'h2' as const,
            title: 'Heading 2' as const,
            class: 'ck-heading_heading2' as const,
          },
          {
            model: 'heading3' as const,
            view: 'h3' as const,
            title: 'Heading 3' as const,
            class: 'ck-heading_heading3' as const,
          },
          {
            model: 'heading4' as const,
            view: 'h4' as const,
            title: 'Heading 4' as const,
            class: 'ck-heading_heading4' as const,
          },
          {
            model: 'heading5' as const,
            view: 'h5' as const,
            title: 'Heading 5' as const,
            class: 'ck-heading_heading5' as const,
          },
          {
            model: 'heading6' as const,
            view: 'h6' as const,
            title: 'Heading 6' as const,
            class: 'ck-heading_heading6' as const,
          },
        ],
      },
      htmlSupport: {
        allow: [
          {
            name: /^.*$/,
          },
        ],
      },
      initialData: '',
      placeholder: placeholder ?? 'Type or paste your content here!',
      image: {
        toolbar: [
          'imageStyle:full',
          'imageStyle:side',
          'imageStyle:inline',
          'imageStyle:wrapText',
          'imageStyle:breakText',
          '|',
          'toggleImageCaption',
          'imageTextAlternative',
          '|',
          'resizeImage',
        ],
      },
      link: {
        addTargetToExternalLinks: true,
        defaultProtocol: 'https://',
      },
      list: {
        properties: {
          styles: true,
          startIndex: true,
          reversed: true,
        },
      },
      menuBar: {
        isVisible: true,
      },
      style: {
        definitions: [
          {
            name: 'Article category',
            element: 'h3',
            classes: ['category'],
          },
          {
            name: 'Title',
            element: 'h2',
            classes: ['document-title'],
          },
          {
            name: 'Subtitle',
            element: 'h3',
            classes: ['document-subtitle'],
          },
          {
            name: 'Info box',
            element: 'p',
            classes: ['info-box'],
          },
          {
            name: 'Side quote',
            element: 'blockquote',
            classes: ['side-quote'],
          },
          {
            name: 'Marker',
            element: 'span',
            classes: ['marker'],
          },
          {
            name: 'Spoiler',
            element: 'span',
            classes: ['spoiler'],
          },
          {
            name: 'Code (dark)',
            element: 'pre',
            classes: ['fancy-code', 'fancy-code-dark'],
          },
          {
            name: 'Code (bright)',
            element: 'pre',
            classes: ['fancy-code', 'fancy-code-bright'],
          },
        ],
      },
      table: {
        contentToolbar: [
          'tableColumn',
          'tableRow',
          'mergeTableCells',
          'tableProperties',
          'tableCellProperties',
        ],
      },
    };

    return (
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, value }, fieldState: { error } }) => {
          return (
            <>
              <Box
                id="editor-box"
                className="editor-main-container"
                sx={{ width: '100% !important', flex: 1 }}
              >
                <Box
                  ref={editorContainerRef}
                  className="editor-container editor-container_classic-editor editor-container_include-style"
                >
                  <Box className="editor-container__editor">
                    <Box ref={editorRef}>
                      {isLayoutReady && (
                        <CKEditor
                          editor={ClassicEditor}
                          data={value || ''}
                          onChange={(event, editor) => {
                            const data = editor.getData();
                            onChange(data);
                            if (onChangeCallback) {
                              onChangeCallback(data);
                            }
                          }}
                          config={editorConfig}
                          {...other}
                        />
                      )}
                      {error && <p style={{ color: 'red' }}>{error.message}</p>}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </>
          );
        }}
      />
    );
  },
);

export default CEditor;
